import React from 'react'
import styled from 'styled-components'
import Layout from '../components/Layout'
import Helmet from '../components/Helmet'
import HeroBlock from '../components/HeroBlock'
import HeroBlockSlide from '../components/HeroBlockSlide'
import { allComponents } from '../components'
import { graphql } from 'gatsby'
import NoticeBar from '../components/NoticeBar'
import { Variables } from '../styles/Variables'
import { MobileView } from '../styles/Utils'

const Wrapper = styled.div`
    background-color: ${Variables.color.lightGrey};
`

export const PageTemplate: React.FC<{
    data?: object
    location?: object
}> = ({ data, location }) => {
    const { metaInformation, heroSection, components, noticeBar, headerBackground } =
        data.allContentfulPage.edges[0].node
    const TitleSuffix = location.pathname !== '/de/' && location.pathname !== '/' ? ' - Pazola' : ''  
    return (
        <Layout location={location} noticeBar={noticeBar} headerBackground={headerBackground}>
            <Helmet
                title={`${metaInformation.pageTitle}${TitleSuffix}`}
                description={metaInformation.description ? metaInformation.description.description : undefined}
            />
            <Wrapper>
                <NoticeBar message={noticeBar} />
                {heroSection && (
                    <HeroBlock noticeBar={noticeBar}>
                        {heroSection.__typename == 'ContentfulHeroBlockSlide' ? (
                            <HeroBlockSlide data={heroSection} />
                        ) : (
                            heroSection.slides.map((slide, i) => {
                                return <HeroBlockSlide key={i} data={slide} />
                            })
                        )}
                    </HeroBlock>
                )}

                {components &&
                    components.map((el, i) => {
                        if (el.sys) {
                            const postfix = el.sys.contentType.sys.id
                            const Component = allComponents[postfix]
                            return !Component ? null : <Component key={i} data={el} />
                        } else {
                            console.log('Not Found')
                            console.log(el)
                        }
                    })}
            </Wrapper>
        </Layout>
    )
}

export const pageQuery = graphql`
    query PageBySlug($id: String!, $locale: String!) {
        allContentfulPage(filter: { id: { eq: $id }, node_locale: { eq: $locale } }) {
            edges {
                node {
                    id
                    noticeBar
                    heroSection {
                        ... on ContentfulHeroBlockSlide {
                            id
                            desktopImage {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            mobileImage {
                                fluid(quality: 90, maxWidth: 750) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            title {
                                title
                            }
                            subtitle
                            alignment
                            invertColor
                        }
                        ... on ContentfulHeroBlock {
                            slides {
                                ... on ContentfulHeroBlockSlide {
                                    id
                                    desktopImage {
                                        fluid(quality: 90, maxWidth: 1680) {
                                            srcWebp
                                            srcSetWebp
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                    mobileImage {
                                        fluid(quality: 90, maxWidth: 750) {
                                            srcWebp
                                            srcSetWebp
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                    title {
                                        title
                                    }
                                    subtitle
                                    alignment
                                    invertColor
                                }
                            }
                        }
                    }
                    components {
                        __typename
                        ... on ContentfulX2ImageCopy {
                            id
                            imageCopy {
                                ... on ContentfulImageCopy {
                                    title
                                    image {
                                        fluid(quality: 90) {
                                            srcWebp
                                            srcSetWebp
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                    # buttons {
                                    #     text
                                    #     link
                                    # }
                                    copy {
                                        childMarkdownRemark {
                                            html
                                        }
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulImageCopy {
                            id
                            title
                            copyVerticalAlignment
                            image {
                                fluid(quality: 90) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            # slideshow {
                            #     ... on ContentfulSlideshow {
                            #         id
                            #         slideshowImages {
                            #             id
                            #             title
                            #             fluid(quality: 90, maxWidth: 1280) {
                            #                 srcWebp
                            #                 srcSetWebp
                            #                 ...GatsbyContentfulFluid
                            #             }
                            #             resize(quality: 90, width: 1660) {
                            #                 src
                            #             }
                            #         }
                            #     }
                            # }
                            fullWidth
                            buttons {
                                text
                                theme
                                buttonLink {
                                    internalFile {
                                        file {
                                            url
                                            contentType
                                        }
                                        title
                                    }
                                    internalLink {
                                        ... on ContentfulSimplePage {
                                            slug
                                        }
                                        ... on ContentfulPage {
                                            slug
                                        }
                                    }
                                    externalLink
                                }
                            }
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            imageCaption {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            layout
                            theme
                            alignment
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulHighlights {
                            id
                            highlights {
                                title
                                highlight
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulCopy {
                            id
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulHeading {
                            id
                            alignment
                            mobileAlignment
                            layout
                            copyWidth
                            titleWidth
                            titleSize
                            headingTitle {
                                headingTitle
                            }
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            buttons {
                                text
                                buttonLink {
                                    internalFile {
                                        file {
                                            url
                                            contentType
                                        }
                                        title
                                    }
                                    internalLink {
                                        ... on ContentfulSimplePage {
                                            slug
                                        }
                                        ... on ContentfulPage {
                                            slug
                                        }
                                    }
                                    externalLink
                                }
                            }
                            backgroundImage {
                                desktopImage {
                                    fluid(quality: 90, maxWidth: 1680) {
                                        srcWebp
                                        srcSetWebp
                                        ...GatsbyContentfulFluid
                                    }
                                }
                                mobileImage {
                                    fluid(quality: 90, maxWidth: 750) {
                                        srcWebp
                                        srcSetWebp
                                        ...GatsbyContentfulFluid
                                    }
                                }
                            }
                            image {
                                desktopImage {
                                    fluid(quality: 90, maxWidth: 1680) {
                                        srcWebp
                                        srcSetWebp
                                        ...GatsbyContentfulFluid
                                    }
                                }
                                mobileImage {
                                    fluid(quality: 90, maxWidth: 750) {
                                        srcWebp
                                        srcSetWebp
                                        ...GatsbyContentfulFluid
                                    }
                                }
                                maxWidth
                            }
                            headingImagePosition
                            backgroundColor
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulX2Heading {
                            id
                            headings {
                                id
                                ... on ContentfulHeading {
                                    id
                                    alignment
                                    layout
                                    copyWidth
                                    titleWidth
                                    titleSize
                                    headingTitle {
                                        headingTitle
                                    }
                                    copy {
                                        childMarkdownRemark {
                                            html
                                        }
                                    }
                                    buttons {
                                        text
                                        buttonLink {
                                            ... on ContentfulLink {
                                                internalFile {
                                                    file {
                                                        url
                                                        contentType
                                                    }
                                                    title
                                                }
                                                internalLink {
                                                    ... on ContentfulSimplePage {
                                                        slug
                                                    }
                                                    ... on ContentfulPage {
                                                        slug
                                                    }
                                                }
                                                externalLink
                                            }
                                        }
                                    }
                                    image {
                                        desktopImage {
                                            fluid(quality: 90, maxWidth: 1680) {
                                                srcWebp
                                                srcSetWebp
                                                ...GatsbyContentfulFluid
                                            }
                                        }
                                        mobileImage {
                                            fluid(quality: 90, maxWidth: 750) {
                                                srcWebp
                                                srcSetWebp
                                                ...GatsbyContentfulFluid
                                            }
                                        }
                                        maxWidth
                                    }
                                    headingImagePosition
                                    sys {
                                        contentType {
                                            sys {
                                                id
                                            }
                                        }
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulQuickLinks {
                            id
                            title
                            image1 {
                                fluid(quality: 90, maxWidth: 640) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            image2 {
                                fluid(quality: 90, maxWidth: 640) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            link1 {
                                ... on ContentfulLink {
                                    internalFile {
                                        file {
                                            url
                                            contentType
                                        }
                                        title
                                    }
                                    internalLink {
                                        ... on ContentfulSimplePage {
                                            slug
                                        }
                                        ... on ContentfulPage {
                                            slug
                                        }
                                    }
                                    externalLink
                                }
                            }
                            link2 {
                                ... on ContentfulLink {
                                    internalFile {
                                        file {
                                            url
                                            contentType
                                        }
                                        title
                                    }
                                    internalLink {
                                        ... on ContentfulSimplePage {
                                            slug
                                        }
                                        ... on ContentfulPage {
                                            slug
                                        }
                                    }
                                    externalLink
                                }
                            }
                            name1
                            name2
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulSlideshow {
                            id
                            slideshowImages {
                                id
                                title
                                fluid(quality: 90, maxWidth: 1280) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                                resize(quality: 90, width: 1660) {
                                    src
                                }
                            }
                            style
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulVideo {
                            id
                            placeholderImage {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            youtubeUrl
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulCollage {
                            title
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            images {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            layout
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulStats {
                            layout
                            desktopImage {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            mobileImage {
                                fluid(quality: 90, maxWidth: 750) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            items {
                                title
                                subtitle
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulAccordion {
                            id
                            items {
                                title
                                copy {
                                    childMarkdownRemark {
                                        html
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulContactForm {
                            id
                            title
                            headings {
                                ... on ContentfulHeading {
                                    id
                                    alignment
                                    titleSize
                                    headingTitle {
                                        headingTitle
                                    }
                                    copy {
                                        childMarkdownRemark {
                                            html
                                        }
                                    }
                                    buttons {
                                        text
                                        theme
                                        buttonLink {
                                            ... on ContentfulLink {
                                                internalFile {
                                                    file {
                                                        url
                                                        contentType
                                                    }
                                                    title
                                                }
                                                internalLink {
                                                    ... on ContentfulSimplePage {
                                                        slug
                                                    }
                                                    ... on ContentfulPage {
                                                        slug
                                                    }
                                                }
                                                externalLink
                                            }
                                        }
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulImage {
                            id
                            desktopImage {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            mobileImage {
                                fluid(quality: 90, maxWidth: 750) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            fullWidth
                            maxWidth
                            parallax
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulX2Copy {
                            copyComponents {
                                copy {
                                    childMarkdownRemark {
                                        html
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulMap {
                            id
                            secondaryMapTitle
                            secondaryMapImage {
                                fluid(quality: 90, maxWidth: 1200) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            slides {
                                id
                                website
                                wind
                                title
                                temperature
                                pinPosition
                                phone
                                location {
                                    lat
                                    lon
                                }
                                image {
                                    id
                                    desktopImage {
                                        fluid(quality: 90, maxWidth: 1680) {
                                            srcWebp
                                            srcSetWebp
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                    mobileImage {
                                        fluid(quality: 90, maxWidth: 750) {
                                            srcWebp
                                            srcSetWebp
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                }
                                email
                                copy {
                                    childMarkdownRemark {
                                        html
                                    }
                                }
                                compass
                            }
                            title
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            image {
                                fluid(quality: 90, maxWidth: 1680) {
                                    srcWebp
                                    srcSetWebp
                                    ...GatsbyContentfulFluid
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        # ... on ContentfulTimeline {
                        #     id
                        #     years {
                        #         year
                        #         milestones {
                        #             title
                        #             date
                        #             copy {
                        #                 copy
                        #             }
                        #         }
                        #     }
                        #     backgroundImage {
                        #         fluid(quality: 90, maxWidth: 1600) {
                        #             srcWebp
                        #             srcSetWebp
                        #             ...GatsbyContentfulFluid
                        #         }
                        #     }
                        #     sys {
                        #         contentType {
                        #             sys {
                        #                 id
                        #             }
                        #         }
                        #     }
                        # }
                        ... on ContentfulAmenitiesSlider {
                            id
                            title
                            copy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            amenities {
                                title
                                copy {
                                    childMarkdownRemark {
                                        html
                                    }
                                }
                                image {
                                    title
                                    thumb: fluid(quality: 90, maxWidth: 1680) {
                                        srcSetWebp
                                        srcSet
                                        ...GatsbyContentfulFluid
                                    }
                                    fullSize: fluid(quality: 90, maxWidth: 1680) {
                                        srcSetWebp
                                        srcSet
                                        ...GatsbyContentfulFluid
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        ... on ContentfulFloorplans {
                            id
                            title
                            floorplans {
                                title
                                copy {
                                    childMarkdownRemark {
                                        html
                                    }
                                }
                                floorplanImages {
                                    title
                                    floorplanPdf {
                                        file {
                                            url
                                        }
                                    }
                                    floorplanAxoImage {
                                        thumb: fluid(quality: 90, maxWidth: 1440) {
                                            srcSetWebp
                                            srcSet
                                            ...GatsbyContentfulFluid
                                        }
                                        fullSize: fluid(quality: 90, maxWidth: 1680) {
                                            srcSetWebp
                                            srcSet
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                    floorplan2dImage {
                                        thumb: fluid(quality: 90, maxWidth: 1440) {
                                            srcSetWebp
                                            srcSet
                                            ...GatsbyContentfulFluid
                                        }
                                        fullSize: fluid(quality: 90, maxWidth: 1680) {
                                            srcSetWebp
                                            srcSet
                                            ...GatsbyContentfulFluid
                                        }
                                    }
                                }
                                renders {
                                    title
                                    fluid(quality: 90, maxWidth: 1680) {
                                        srcSetWebp
                                        srcSet
                                        ...GatsbyContentfulFluid
                                    }
                                }
                                pricelist {
                                    ... on ContentfulPricelist {
                                        title
                                        pricelistPdf {
                                            file {
                                                url
                                            }
                                        }
                                        items {
                                            availability
                                            price
                                            floorplans {
                                                file {
                                                    url
                                                }
                                            }
                                            sqMetres
                                            unit
                                            unitNo
                                            floor
                                            bedrooms
                                            unitType
                                            balcony
                                        }
                                    }
                                }
                            }
                            sys {
                                contentType {
                                    sys {
                                        id
                                    }
                                }
                            }
                        }
                        # ... on ContentfulPricelist {
                        #     title
                        #     pricelistPdf {
                        #         file {
                        #             url
                        #         }
                        #     }
                        #     items {
                        #         availability
                        #         price
                        #         floorplans {
                        #             file {
                        #                 url
                        #             }
                        #         }
                        #         sqMetres
                        #         unit
                        #         unitNo
                        #         floor
                        #         bedrooms
                        #         unitType
                        #         balcony
                        #     }
                        #     sys {
                        #         contentType {
                        #             sys {
                        #                 id
                        #             }
                        #         }
                        #     }
                        # }
                    }
                    metaInformation {
                        pageTitle
                        description {
                            description
                        }
                    }
                    slug
                    node_locale
                    headerBackground
                }
            }
        }
    }
`

export default PageTemplate
